import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import app from "../firebase";
import config from "../config";
import { handlePushSessionRevoke } from "../api/axiosAuthInterceptor";

const DEVICE_ID_KEY = "routeeye_web_device_id";

function getOrCreateDeviceId() {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = `web_${crypto.randomUUID()}`;
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
}

function resolveUserId(userId) {
  if (userId) return String(userId);
  try {
    const userState = JSON.parse(localStorage.getItem("userState") || "{}");
    if (userState?.user?.id) return String(userState.user.id);
  } catch {
    /* ignore */
  }
  return null;
}

let pushListenersInstalled = false;
/** Coalesce concurrent registerPushNotifications calls (login + bootstrap). */
let registrationInFlight = null;
/** Skip duplicate POST when the same user/token was already registered this session. */
let lastRegistered = { userId: null, token: null };

const FCM_SW_PATH = "/firebase-messaging-sw.js";

export function clearPushRegistrationCache() {
  registrationInFlight = null;
  lastRegistered = { userId: null, token: null };
}

/**
 * Start permission request during Sign-in click (user gesture). Do not await before
 * login fetch — run login and the permission dialog in parallel, then await the
 * result only after a successful login.
 */
export function beginNotificationPermissionRequest() {
  if (typeof Notification === "undefined") {
    console.warn("[push] Notifications API not available in this browser");
    return Promise.resolve("unsupported");
  }

  const before = Notification.permission;
  console.info("[push] permission before prompt:", before);

  if (before === "granted") {
    console.info(
      "[push] Notifications are already allowed for this site — the browser will not show Allow/Block again. To see the prompt again: Chrome → Site settings → Notifications → find this site → Reset.",
    );
    return Promise.resolve(before);
  }

  if (before === "denied") {
    console.info(
      "[push] Notifications are blocked for this site. Allow them in Chrome → Site settings → Notifications.",
    );
    return Promise.resolve(before);
  }

  // Edge can leave requestPermission pending forever when the prompt is never shown.
  // Login must not wait on that.
  const requestPromise = Promise.resolve()
    .then(() => Notification.requestPermission())
    .then((result) => {
      console.info("[push] permission after prompt:", result);
      return result;
    })
    .catch((error) => {
      console.warn(
        "[push] notification permission failed:",
        error?.message || error,
      );
      return "denied";
    });

  const timeoutPromise = new Promise((resolve) => {
    setTimeout(() => {
      console.info(
        "[push] permission prompt timed out; continuing without it",
      );
      resolve("default");
    }, 1500);
  });

  return Promise.race([requestPromise, timeoutPromise]);
}

/** @deprecated Use beginNotificationPermissionRequest — kept for imports */
export async function promptForNotificationPermissionOnLoginClick() {
  return beginNotificationPermissionRequest();
}

function showForegroundNotification(payload) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") {
    return;
  }

  const data = payload?.data || {};
  const title =
    data.title || payload?.notification?.title || "RouteEye";
  const body = data.body || payload?.notification?.body || "";

  if (!title && !body) {
    return;
  }

  try {
    // eslint-disable-next-line no-new
    new Notification(title, {
      body,
      icon: "/logo.svg",
      data,
    });
  } catch (error) {
    console.warn("[push] Foreground notification failed:", error?.message || error);
  }
}

function waitForServiceWorkerActivation(registration, timeoutMs = 15000) {
  if (registration.active) {
    return Promise.resolve(registration);
  }

  const worker = registration.installing || registration.waiting;
  if (!worker) {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("Service worker activation timed out"));
      }, timeoutMs);

      const finish = (reg) => {
        clearTimeout(timeout);
        if (reg?.active) {
          resolve(reg);
        } else {
          reject(new Error("Service worker failed to activate"));
        }
      };

      navigator.serviceWorker.ready
        .then(() => navigator.serviceWorker.getRegistration(FCM_SW_PATH))
        .then((reg) => finish(reg || registration))
        .catch(reject);
    });
  }

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error("Service worker activation timed out"));
    }, timeoutMs);

    const onStateChange = () => {
      if (worker.state === "activated") {
        clearTimeout(timeout);
        worker.removeEventListener("statechange", onStateChange);
        resolve(registration);
      } else if (worker.state === "redundant") {
        clearTimeout(timeout);
        worker.removeEventListener("statechange", onStateChange);
        reject(new Error("Service worker became redundant"));
      }
    };

    worker.addEventListener("statechange", onStateChange);
    onStateChange();
  });
}

async function getFirebaseMessagingServiceWorker() {
  if (typeof navigator === "undefined" || !navigator.serviceWorker) {
    return null;
  }

  try {
    let registration = await navigator.serviceWorker.getRegistration(FCM_SW_PATH);
    if (!registration) {
      registration = await navigator.serviceWorker.register(FCM_SW_PATH);
    }

    registration = await waitForServiceWorkerActivation(registration);

    if (!registration?.active) {
      console.warn("[push] Service worker registered but not active");
      return null;
    }

    console.info("[push] Service worker active:", registration.active.scriptURL);
    return registration;
  } catch (error) {
    console.warn("[push] Service worker registration failed:", error?.message || error);
    return null;
  }
}

/** Register FCM service worker as early as possible (before login). */
export function preloadFirebaseMessagingServiceWorker() {
  return getFirebaseMessagingServiceWorker();
}

/**
 * Foreground FCM + service worker messages (e.g. password_updated → force logout).
 */
export async function setupPushNotificationListeners() {
  if (pushListenersInstalled) {
    return;
  }

  try {
    if (!(await isSupported())) {
      return;
    }

    await getFirebaseMessagingServiceWorker();

    const messaging = getMessaging(app);
    onMessage(messaging, (payload) => {
      console.info("[push] Foreground FCM message:", payload?.data || payload);
      showForegroundNotification(payload);
      handlePushSessionRevoke(payload);
    });

    if (typeof navigator !== "undefined" && navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data?.type === "FCM_SESSION_REVOKED") {
          handlePushSessionRevoke({ data: event.data });
        }
      });
    }

    pushListenersInstalled = true;
  } catch (error) {
    console.warn("[push] Listener setup failed:", error?.message || error);
  }
}

/**
 * Ask for browser notification permission, obtain FCM token, register with notification-service.
 * Call after successful login when user id is known.
 */
async function registerPushNotificationsOnce(userId) {
  const resolvedUserId = resolveUserId(userId);
  const serviceUrl = config.notificationServiceURL;
  const vapidKey = config.firebaseVapidKey;

  if (!resolvedUserId) {
    console.warn("[push] No user id available for token registration");
    return false;
  }

  if (!serviceUrl || !vapidKey) {
    console.warn(
      "[push] Set REACT_APP_NOTIFICATION_SERVICE_URL and REACT_APP_FIREBASE_VAPID_KEY to enable push",
    );
    return false;
  }

  try {
    if (!(await isSupported())) {
      console.warn("[push] Firebase messaging is not supported in this browser");
      return false;
    }

    const permission = Notification.permission;
    console.info("[push] permission at registration:", permission);

    if (permission === "default") {
      console.warn(
        "[push] Permission still 'default' — allow/block prompt must run on Login click (before API). Re-login and click Allow when prompted.",
      );
      return false;
    }

    if (permission !== "granted") {
      console.warn(
        `[push] Notification permission not granted (${permission}). Reset at chrome://settings/content/notifications for this site.`,
      );
      return false;
    }

    const serviceWorkerRegistration = await getFirebaseMessagingServiceWorker();
    if (!serviceWorkerRegistration) {
      console.warn("[push] Cannot register push without an active service worker");
      return false;
    }

    const messaging = getMessaging(app);
    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration,
    });

    if (!token) {
      console.warn("[push] FCM token was empty");
      return false;
    }

    if (
      lastRegistered.userId === resolvedUserId &&
      lastRegistered.token === token
    ) {
      console.info(
        "[push] FCM token unchanged for user",
        resolvedUserId,
        "— skipping duplicate /api/tokens call",
      );
      return true;
    }

    const response = await fetch(`${serviceUrl.replace(/\/$/, "")}/api/tokens`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: resolvedUserId,
        device_id: getOrCreateDeviceId(),
        platform: "web",
        notification_token: token,
      }),
    });

    if (!response.ok) {
      const text = await response.text();
      console.warn("[push] Token registration failed:", response.status, text);
      return false;
    }

    lastRegistered = { userId: resolvedUserId, token };
    console.info("[push] Token registered for user", resolvedUserId);
    return true;
  } catch (error) {
    console.warn("[push] Registration failed:", error?.message || error);
    return false;
  }
}

/**
 * Ask for browser notification permission, obtain FCM token, register with notification-service.
 * Call after successful login when user id is known. Concurrent callers share one run.
 */
export async function registerPushNotifications(userId) {
  if (registrationInFlight) {
    console.info("[push] Registration already in progress — reusing same attempt");
    return registrationInFlight;
  }

  registrationInFlight = registerPushNotificationsOnce(userId).finally(() => {
    registrationInFlight = null;
  });

  return registrationInFlight;
}
